import { Transition } from "history";
import { useCallback, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ReportSummaryItemApi } from "../api/Reports";
import { useReportByUUID } from "../api/ReportsApi";
import { useActorInfoQuery } from "../api/UsersApi";
import { useBlocker } from "../utils/blocker";
import ReportAuxiliaryUnlockHook from "./ReportAuxiliaryUnlockHook";
import {
  getFallbackWorkflowRunUuid,
  ReportAuxiliaryLocationState,
  resolveAuxiliaryReportLoading,
} from "./reportNavigationUnlock";
import { isActorIdentityPendingForLock, isReportLockedByOther } from "./utils";
import { useReportUnlock } from "./useReportUnlock";

type UseReportAuxiliaryLeaveUnlockOptions = {
  report?: ReportSummaryItemApi;
  isReportError?: boolean;
  isReportLoading?: boolean;
};

export function useReportAuxiliaryLeaveUnlock(
  options?: UseReportAuxiliaryLeaveUnlockOptions
) {
  const { id } = useParams();
  const location = useLocation();
  const locationState = location.state as ReportAuxiliaryLocationState | null;
  const locationIsReadOnly = locationState?.isReadOnly;
  const fallbackWorkflowRunUuid = getFallbackWorkflowRunUuid(location.state);
  const parentProvidesReport = options != null && "report" in options;
  const {
    data: fetchedReport,
    isError: fetchedReportError,
    isLoading: fetchedReportLoading,
    mutate: reportGet,
  } = useReportByUUID();
  const report = parentProvidesReport
    ? options?.report
    : options?.report ?? fetchedReport;
  const isReportError = options?.isReportError ?? fetchedReportError;
  const isReportLoading = resolveAuxiliaryReportLoading(
    id,
    report,
    isReportError,
    parentProvidesReport,
    options?.isReportLoading,
    fetchedReportLoading
  );
  const { data: actorInfo, isLoading: isActorInfoLoading } =
    useActorInfoQuery(true);
  const {
    performUnlock,
    error: errorUnlockingReport,
    modal,
  } = useReportUnlock();

  useEffect(() => {
    if (id && !parentProvidesReport) {
      reportGet(id);
    }
  }, [reportGet, id, parentProvidesReport]);

  const isActorIdentityPending = isActorIdentityPendingForLock(
    isActorInfoLoading,
    locationIsReadOnly,
    report
  );
  const isLockedByOther = isActorIdentityPending
    ? false
    : isReportLockedByOther(report, actorInfo?.actor_uuid);
  const isReadOnly =
    locationIsReadOnly === true ||
    (locationIsReadOnly !== false && isLockedByOther);

  const blocker = useCallback(
    (tx: Transition) =>
      ReportAuxiliaryUnlockHook(
        tx,
        isReadOnly,
        isLockedByOther,
        report,
        id,
        performUnlock,
        !!errorUnlockingReport,
        isReportLoading,
        isActorIdentityPending,
        fallbackWorkflowRunUuid
      ),
    [
      isReadOnly,
      isLockedByOther,
      report,
      id,
      performUnlock,
      errorUnlockingReport,
      isReportLoading,
      isActorIdentityPending,
      fallbackWorkflowRunUuid,
    ]
  );

  useBlocker(blocker);

  return { modal, errorUnlockingReport, report };
}
